<template>
  <section
    id="county-search-form"
    class="pt-0 text-center d-print-none pb-3"
    :class="{
      'px-0': isMobile === 'mobile',
      'px-3 mt-3 pt-3': isMobile === 'desktop',
    }"
  >
    <div
      class="d-flex mt-0 w-100"
      id="landing-search-container"
      :class="isMobile === 'mobile' ? 'flex-column' : ''"
    >
      <div
        v-if="isMobile === 'desktop' && !resultsPresent"
        id="county-search-landing "
        class="d-flex flex-column w-50"
      >
        <div>
          <h1 id="county-name" class="text-center lh-1 mb-0 text-primary">
            {{ county.name }}
          </h1>
          <h1 class="text-center lh-1 text-secondary">County</h1>
        </div>
        <div id="county-courthouse-search-form" class="mb-4">
          <county-courthouse class="w-50" />
        </div>
      </div>

      <form
        autocomplete="off"
        class="d-flex flex-column "
        :class="
          isMobile === 'mobile'
            ? 'w-100 p-4'
            : !resultsPresent
            ? 'w-50 pb-0'
            : 'w-100'
        "
        @submit.prevent="$emit('submit', formData)"
      >
        <div class="d-flex flex-column justify-content-center">
          <h3 class="lh-1 mb-0 h-auto">Search for your property</h3>
        </div>

        <div
          id="search-form"
          class="d-flex"
          :class="
            !resultsPresent || isMobile === 'mobile'
              ? 'flex-column'
              : 'justify-content-around'
          "
        >
          <div class="my-2">
            <label for="owner-name" class="form-label">Name</label><br />
            <auto-suggest
              :corp-code="county.corpCode"
              id="owner-name"
              :componentId="'owner-name'"
              name="ownerName"
              placeholder="ex: Smith, John"
              :url="lookupApiUrl('NameAutoComplete')"
              urlParamName="name"
              @valueChanged="updateOwnerName"
              :isCleared="cleared.ownerName"
              :backgroundColor="backgroundColor"
            />
          </div>

          <div class="my-2">
            <label for="address" class="form-label">Address</label><br />
            <auto-suggest
              :corp-code="county.corpCode"
              id="address"
              :componentId="'address'"
              name="address"
              placeholder="ex: 210 N Ironwood"
              :url="lookupApiUrl('AddressAutoComplete')"
              urlParamName="address"
              @valueChanged="updateAddress"
              :isCleared="cleared.address"
              :backgroundColor="backgroundColor"
            />
          </div>

          <div class="my-2">
            <label for="parcel-number" class="form-label">Parcel Number</label
            ><br />
            <input
              :value="parcelNumber"
              @input="updateParcelNumber"
              class="form-control form-control-sm"
              :class="!cleared.parcelNumber ? backgroundColor : 'bg-white'"
              data-lpignore="true"
              id="parcel-number"
              placeholder="ex: 71-09-08-103-008.000-026"
              type="text"
            />
          </div>

          <div class="my-2">
            <label for="duplicate-number" class="form-label d-flex"
              ><span>Duplicate</span>&nbsp;
              <span class="d-sm-none d-md-block">Number</span> </label
            ><br />
            <input
              :value="duplicateNumber"
              @input="updateDuplicateNumber"
              class="form-control form-control-sm"
              :class="!cleared.duplicateNumber ? backgroundColor : 'bg-white'"
              data-lpignore="true"
              id="duplicate-number"
              placeholder="ex: 79910"
              type="text"
            />
          </div>

          <div class="my-2 d-flex justify-content-center">
            <search-button
              v-if="!searchBusy"
              class="mt-0"
              :searchBusy="searchBusy"
              :updateCleared="updateCleared"
              :recentSearches="
                recentSearches && recentSearches.size ? true : false
              "
              :recentParcels="
                recentParcels && recentParcels.size ? true : false
              "
              :currentPayYear="currentPayYear"
              :getAndSetSystemParameters="getAndSetSystemParameters"
            />
            <button
              v-else
              class="dropdown-button rounded flex-button button-label border-0"
              @click.prevent="$emit('cancel')"
              type="button"
            >
              <span
                class="spinner-border spinner-border-sm"
                role="status"
              ></span>
              &nbsp;&nbsp;
              <span>CANCEL</span>
            </button>
          </div>
        </div>
      </form>
    </div>
    <div class="row">
      <div
        class="col text-center text-black"
        :class="isMobile === 'mobile' ? 'mx-3 mb-3' : 'mx-0'"
      >
        {{
          !resultsPresent
            ? "Search for property tax information using one or more of the above fields."
            : ""
        }}
        <a
          href="#"
          class="text-black text-nowrap help-link d-flex align-items-center justify-content-center"
          data-bs-toggle="modal"
          data-bs-target="#search-tips-modal"
        >
          <QuestionCircleSVG />
          &nbsp; <span>Search tips!</span></a
        >
      </div>
    </div>
    <!-- desktop only -->
    <recently-viewed-module
      v-if="recentSearches && recentSearches.size && isMobile === 'desktop'"
      :modalId="'recent-searches-modal'"
      :title="'Recent Searches'"
    >
      <div class="table-responsive">
        <table class="table table-sm">
          <thead>
            <tr class="border-bottom border-dark">
              <th class="fs-5 text-center">Name</th>
              <th class="fs-5 text-center">Address</th>
              <th class="fs-5 text-center">Parcel</th>
              <th class="fs-5 text-center">Duplicate</th>
            </tr>
          </thead>
          <tbody>
            <recently-viewed-search
              v-for="(search, index) in Array.from(
                this.recentSearches.entries()
              ).reverse()"
              :key="index"
              :search="search[1]"
              :query="JSON.parse(search[0])"
              :isMobile="isMobile"
              :index="index"
              :updateFormFields="updateFormFields"
              :status="status"
              :updateStatus="updateStatus"
              :countySlug="county.slug"
              :hideBackdrop="hideBackdrop"
            />
          </tbody>
        </table>
      </div>
    </recently-viewed-module>
    <!-- mobile only -->
    <recently-viewed-module
      v-else-if="recentSearches && recentSearches.size"
      :modalId="'recent-searches-modal'"
      :title="'Recent Searches'"
    >
      <recently-viewed-search
        v-for="(search, index) in Array.from(
          this.recentSearches.entries()
        ).reverse()"
        :key="index"
        :search="search[1]"
        :query="JSON.parse(search[0])"
        :isMobile="isMobile"
        :index="index"
        :updateFormFields="updateFormFields"
        :status="status"
        :updateStatus="updateStatus"
        :countySlug="county.slug"
        :hideBackdrop="hideBackdrop"
      />
    </recently-viewed-module>

    <recently-viewed-module
      v-if="recentParcels && recentParcels.size"
      :modalId="'recent-parcels-modal'"
      :title="'Recently Viewed Parcels'"
    >
      <search-results
        :class="isMobile"
        :isMobile="isMobile"
        :county="county"
        :items="Array.from(recentParcels.values()).reverse()"
        :pagination="pagination"
        :status="'recentParcels'"
        :updateRecentParcels="updateRecentParcels"
        :currentPayYear="currentPayYear"
        :hideBackdrop="hideBackdrop"
      />
    </recently-viewed-module>
    <county-search-tips-modal />
  </section>
</template>

<style lang="scss" scoped>
@import "../../assets/sass/custom";

#search-form label {
  margin-bottom: 0;
  font-weight: bold;
  float: left;
}
::-webkit-input-placeholder {
  /* Edge */
  color: #ccc;
}
:-ms-input-placeholder {
  /* Internet Explorer 10-11 */
  color: #ccc;
}
::placeholder {
  color: #ccc;
}
#names,
#addresses {
  width: 275px;
  z-index: 1070;
}
#names button:hover,
#addresses button:hover {
  background-color: #efefef;
}
#county-name {
  font-size: 4rem;
}
thead th {
  width: 20%;
}
.help-link {
  text-decoration: none;
}
.help-link:hover span {
  text-decoration: underline;
}
.search-tooltip {
  display: none;
  position: fixed;
  height: 0;
  width: 0;
  border-style: solid;
  border-width: 2px;
}
</style>

<script>
import AutoSuggest from "../common/AutoSuggest.vue";
import RecentlyViewedModule from "./RecentlyViewedModal.vue";
import SearchButton from "./SearchButton.vue";
import CountySearchTipsModal from "./SearchTipsModal.vue";
import RecentlyViewedSearch from "./RecentlyViewedSearch.vue";
import SearchResults from "./SearchResults.vue";
import CountyCourthouse from "../svg/CountyCourthouse.vue";
import QuestionCircleSVG from "../svg/QuestionCircleSVG.vue";
import { deepCopy } from "../../utilities";
import * as bootstrap from "bootstrap";
export default {
  components: {
    AutoSuggest,
    CountySearchTipsModal,
    CountyCourthouse,
    RecentlyViewedModule,
    RecentlyViewedSearch,
    SearchButton,
    SearchResults,
    QuestionCircleSVG,
  },
  data() {
    return {
      address: null,
      autoCompleteAddresses: [],
      autoCompleteNames: [],
      duplicateNumber: null,
      ownerName: "",
      parcelNumber: null,
      cleared: {
        address: true,
        duplicateNumber: true,
        ownerName: true,
        parcelNumber: true,
      },
    };
  },
  emits: [
    "submit", // emit data to parent (see form @submit)
    "cancel",
  ],
  methods: {
    hideBackdrop(modalId) {
      const modal = bootstrap.Modal.getInstance(
        document.getElementById(modalId)
      );
      modal.hide();
      const backdrops = document.getElementsByClassName("modal-backdrop");

      for (let i = 0; i < backdrops.length; i += 1) {
        backdrops[i].style.display = "none";
      }
    },
    lookupApiUrl(method) {
      return process.env.VUE_APP_DATASERVICE + method;
    },
    updateOwnerName(val) {
      this.ownerName = val;
      this.cleared.ownerName = true;
    },
    updateAddress(val) {
      this.address = val;
      this.cleared.address = true;
    },
    updateParcelNumber(event) {
      this.parcelNumber = event.target.value.trim();
      this.cleared.parcelNumber = true;
    },
    updateDuplicateNumber(event) {
      this.duplicateNumber = event.target.value.trim();
      this.cleared.duplicateNumber = true;
    },
    updateFormFields(newFields) {
      this.ownerName = newFields.ownerName || "";
      this.address = newFields.address || null;
      this.parcelNumber = newFields.parcelNumber || null;
      this.duplicateNumber = newFields.duplicateNumber || null;
      this.updateCleared();
    },
    updateCleared() {
      this.cleared = {
        address: !this.address,
        duplicateNumber: !this.duplicateNumber,
        ownerName: !this.ownerName,
        parcelNumber: !this.parcelNumber,
      };
    },
    deepCopy: deepCopy,
  },
  computed: {
    formData() {
      const copy = this.deepCopy(this.$data);
      delete copy.cleared;
      return copy;
    },
    pagination() {
      return {
        elapsedTime: 0,
        numRecords: this.recentParcels.size,
        page: 0,
        perPage: 15,
      };
    },
  },
  mounted() {
    window.bootstrap = bootstrap;
    this.$nextTick(function() {
      this.address = this.$route.query.address;
      this.duplicateNumber = this.$route.query.duplicateNumber;
      this.ownerName = this.$route.query.ownerName;
      this.parcelNumber = this.$route.query.parcelNumber;
    });
    this.cleared.address = !this.$route.query.address;
    this.cleared.duplicateNumber = !this.$route.query.duplicateNumber;
    this.cleared.ownerName = !this.$route.query.ownerName;
    this.cleared.parcelNumber = !this.$route.query.parcelNumber;
  },
  props: {
    county: {
      type: Object,
      required: true,
    },
    searchBusy: {
      type: Boolean,
      required: true,
    },
    isMobile: {
      type: String,
      required: true,
    },
    backgroundColor: {
      type: String,
      required: false,
    },
    recentSearches: {
      type: Map,
      required: false,
    },
    recentParcels: {
      type: Map,
      required: false,
    },
    updateRecentParcels: {
      type: Function,
      required: true,
    },
    currentPayYear: {
      type: String,
      required: false,
    },
    getAndSetSystemParameters: {
      type: Function,
      required: true,
    },
    resultsPresent: {
      type: Boolean,
      required: true,
    },
    status: {
      // ready|cancelled|searching|error|recentParcels
      type: String,
      required: true,
    },
    updateStatus: {
      type: Function,
      required: true,
    }
  },
};
</script>
